import { useEffect, useRef } from 'react';
import { useAccountsRefetch } from '@/entities/account';
import { useSessionStore } from '@/entities/session';
import { useExecuteOnce } from '@/shared/lib';
import {
  usePendingAccountsStore,
  type PendingAccount,
} from './pendingAccountsStore';

interface UseAccountOpeningTimerReturn {
  pendingAccounts: PendingAccount[];
  addPendingAccount: (
    accountNumber: string,
    accountType: 'spot' | 'invest' | 'margin' | 'earn',
    timeoutSeconds: number,
    assetCode?: string
  ) => void;
  removePendingAccount: (accountNumber: string) => void;
}

const useAccountOpeningTimer = (): UseAccountOpeningTimerReturn => {
  const {
    pendingAccounts,
    addPendingAccount,
    removePendingAccount,
    updateRemainingTime,
    clearExpiredAccounts,
  } = usePendingAccountsStore();

  const { session } = useSessionStore();
  const { refetchAll } = useAccountsRefetch({ userId: session?.userId });
  const intervalRef = useRef<number | null>(null);

  useExecuteOnce(() => {
    clearExpiredAccounts();
  });

  useEffect(() => {
    if (pendingAccounts.length === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = window.setInterval(() => {
      const prevCount = pendingAccounts.length;
      updateRemainingTime();

      const currentState = usePendingAccountsStore.getState();
      if (currentState.pendingAccounts.length < prevCount) {
        if (session?.userId) {
          refetchAll();
        }
      }
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    pendingAccounts.length,
    refetchAll,
    session?.userId,
    updateRemainingTime,
  ]);

  return {
    pendingAccounts,
    addPendingAccount,
    removePendingAccount,
  };
};

export { useAccountOpeningTimer };
